import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Breadcrumbs from "@/components/vr/Breadcrumbs";
import Seo, { breadcrumbLd } from "@/components/vr/Seo";

export function SubjectsList() {
  const [subjects, setSubjects] = useState([]);
  useEffect(() => { api.get("/subjects").then((r) => setSubjects(r.data)); }, []);
  return (
    <div className="vr-container my-8">
      <Seo
        title="All Subjects — Bihar Board Class 9-12"
        description="बिहार बोर्ड सभी विषयों की सम्पूर्ण तैयारी — Hindi, English, Mathematics, Science, Social Science, Physics, Chemistry, Biology, History, Geography।"
        canonical="/subjects"
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: "Subjects", url: "/subjects" }])}
      />
      <Breadcrumbs items={[{ label: "Subjects" }]} />
      <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">All Subjects</h1>
      <p className="text-slate-600 mt-2 font-hindi">प्रत्येक विषय के लिए Chapter-wise Notes, MCQ एवं Videos।</p>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {subjects.map((s) => (
          <Link key={s.id} to={`/subjects/${s.slug}`} className="vr-card p-4" data-testid={`sub-${s.slug}`}>
            <h2 className="font-display font-bold text-[#0F172A] text-base">{s.name}</h2>
            <div className="text-sm text-slate-500 font-hindi">{s.hindi_name}</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {s.class_slugs?.map((c) => <span key={c} className="vr-chip">{c.replace("class-", "Class ")}</span>)}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function SubjectDetail() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const cls = sp.get("class");
  const [sub, setSub] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    api.get(`/subjects/${slug}`).then((r) => setSub(r.data));
    const suffix = cls ? `?class_slug=${cls}&subject_slug=${slug}` : `?subject_slug=${slug}`;
    api.get(`/chapters${suffix}`).then((r) => setChapters(r.data));
    api.get(`/notes${suffix}`).then((r) => setNotes(r.data));
  }, [slug, cls]);
  if (!sub) return <div className="vr-container my-10 text-slate-500">Loading...</div>;
  const canonical = `/subjects/${sub.slug}`;
  const title = sub.meta_title || `${sub.name} — Bihar Board Notes, MCQ & Chapters`;
  const desc = sub.meta_description || `${sub.name} (${sub.hindi_name || ""}) के लिए Bihar Board के सम्पूर्ण Notes, MCQ Quiz एवं Chapters।`;
  return (
    <div className="vr-container my-8">
      <Seo title={title} description={desc} canonical={canonical}
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: "Subjects", url: "/subjects" }, { name: sub.name, url: canonical }])} />
      <Breadcrumbs items={[{ to: "/subjects", label: "Subjects" }, { label: sub.name }]} />
      <div className="vr-card p-6">
        <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">{sub.name}</h1>
        <div className="text-slate-500 font-hindi text-lg">{sub.hindi_name}</div>
        <div className="mt-2 flex flex-wrap gap-1">
          {sub.class_slugs?.map((c) => <Link key={c} to={`/${c}`} className="vr-chip hover:bg-blue-100">{c.replace("class-", "Class ")}</Link>)}
        </div>
      </div>

      <h2 className="mt-8 font-display font-extrabold text-xl">Chapters</h2>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {chapters.map((ch) => (
          <div key={ch.id} className="vr-card p-4" data-testid={`sub-chap-${ch.slug}`}>
            <h3 className="font-semibold text-[#0F172A] text-base">{ch.title}</h3>
            <div className="text-xs text-slate-500 font-hindi mt-1">{ch.summary}</div>
          </div>
        ))}
        {chapters.length === 0 && <div className="text-slate-500 text-sm">जल्द ही अपलोड होंगे।</div>}
      </div>

      <h2 className="mt-8 font-display font-extrabold text-xl">Notes</h2>
      <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {notes.map((n) => (
          <Link key={n.id} to={`/notes/${n.slug}`} className="vr-card p-4" data-testid={`sub-note-${n.slug}`}>
            <h3 className="font-semibold text-[#0F172A] text-base">{n.title}</h3>
            <div className="text-xs text-slate-500 mt-1 font-hindi line-clamp-2">{n.intro}</div>
          </Link>
        ))}
        {notes.length === 0 && <div className="text-slate-500 text-sm col-span-3">जल्द ही अपलोड होंगे।</div>}
      </div>
    </div>
  );
}
